/**
 * Live badge counts for the new-tab launcher rows.
 *
 * Three cheap, independent reads — each one is best-effort: a failure (or a
 * missing permission, which comes back as a 403) simply leaves that badge
 * absent rather than failing the page. Counts are keyed by the launcher's
 * route path so the row rendering stays a plain lookup.
 *
 *   /agent/conversation/inbox            → unread conversations in "Mine"
 *                                          scope (same query the inbox itself
 *                                          uses, so the badge matches the tab)
 *   /agent/conversation/omnichannel-queue → pending, unassigned interactions
 *   /agent/chats                          → active live chats
 *
 * Refreshed on mount and every REFRESH_MS while the tab is visible.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'

const REFRESH_MS = 30_000

/** Route path → count. A path is absent when its read failed or is unavailable. */
export type LauncherCounts = Record<string, number>

interface ConversationCounts {
  unread?: number
}
interface QueueResponse {
  items?: unknown[]
}
interface LiveChatsResponse {
  chats?: { status?: string }[]
}

/** Resolves to `null` instead of rejecting — badges are a nicety, not critical. */
async function safe<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p
  } catch {
    return null
  }
}

export function useLauncherCounts(userId: string): LauncherCounts {
  const [counts, setCounts] = useState<LauncherCounts>({})
  const liveRef = useRef(true)

  const refreshInboxAndQueue = useCallback(async (): Promise<void> => {
    const inboxQuery = userId
      ? `/conversations/counts?assignedAgentIdOrQueueMember=${encodeURIComponent(userId)}`
      : '/conversations/counts'

    const [inbox, queue] = await Promise.all([
      safe(api.get<ConversationCounts>(inboxQuery)),
      safe(api.get<QueueResponse>('/omnichannel-queue?status=true')),
    ])

    const next: LauncherCounts = {}
    if (inbox && typeof inbox.unread === 'number') {
      next['/agent/conversation/inbox'] = inbox.unread
    }
    if (queue?.items) {
      next['/agent/conversation/omnichannel-queue'] = queue.items.length
    }
    if (liveRef.current) setCounts((prev) => ({ ...prev, ...next }))
  }, [userId])

  const refreshLiveChatCount = useCallback(async (): Promise<void> => {
    const chats = await safe(api.get<LiveChatsResponse>('/agent/live-chats'))
    if (!liveRef.current) return
    const count = chats?.chats ? chats.chats.filter((c) => c.status === 'active').length : 0
    setCounts((prev) => ({ ...prev, ['/agent/chats']: count }))
  }, [])

  useEffect(() => {
    liveRef.current = true
    const tick = (): void => {
      if (document.hidden) return
      void refreshInboxAndQueue()
    }
    tick()
    const id = window.setInterval(tick, REFRESH_MS)

    // Live chat count is driven by the `live:chat:list:bump` socket event
    // (emitted on new message, escalation assigned, finalize) instead of
    // the 30s poll. Initial fetch backfills on mount / refresh.
    void refreshLiveChatCount()
    const socket = getSocket()
    let timer: ReturnType<typeof setTimeout> | null = null
    const onBump = (): void => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void refreshLiveChatCount(), 400)
    }
    socket.on('live:chat:list:bump', onBump)

    return () => {
      liveRef.current = false
      window.clearInterval(id)
      if (timer) clearTimeout(timer)
      socket.off('live:chat:list:bump', onBump)
    }
  }, [refreshInboxAndQueue, refreshLiveChatCount])

  return counts
}
